import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import styles from "./Style.module.css";
import { Header } from "../../Components/Header/Header";
import Timer from "../../Components/Timer/Timer";
import { SessionExpiredPopup } from "../../Components/Popup/SessionExpiredPopup/SessionExpiredPopup";
import { SlotUnavailablePopup } from "../../Components/Popup/SlotUnavailablePopup/SlotUnavailablePopup";
import { BlockedCustomerPopup } from "../../Components/Popup/BlockedCustomerPopup/BlockedCustomerPopup";
import { isBlockedCustomerResponse } from "../../Utils/blockedCustomer";
import { useReleaseBookingHold } from "../../Utils/Hooks/useReleaseBookingHold";
import { useCustomer } from "../../Context/Customer/CustomerContext";
import { useAppointment } from "../../Context/Appointment/AppointmentContext";
import { useBookingTimer } from "../../Context/Timer/useBookingTimer";
import { useSticky } from "../../Utils/Helper";
import { useHoldExpiry } from "../../Utils/Hooks/useHoldExpiry";
import { useValidateBookingSession } from "../../Utils/Hooks/useValidateBookingSession";
import { OTP_RESEND_COOLDOWN_SECONDS } from "../../Utils/bookingVerification";
import { getCountryIdByIsoCode } from "../../Utils/countries";
import {
  getSessionStorageValue,
  removeSessionStorageValue,
  setSessionStorageValue,
  STORAGE_KEYS,
} from "../../Utils/SessionStorage";
import { useGetCountries, useVerifyOTP, useSendSMS, useGetCustomerDetails } from "../../APICalling/useApiQueries";

import { VerificationLayout } from "../../Components/VerificationLayout/VerificationLayout";
import { navigateWithInputFocus } from "../../Utils/navigateWithInputFocus";

import { otpResponseError } from "../../Utils/otpAttempts";
import { useOtpApiAttempts } from "../../Utils/Hooks/useOtpApiAttempts";
import { setOtpSession } from "../../Utils/otpSession";
import { useBookingSession } from "../../Context/BookingSession/BookingSessionContext";
import { useCreateBookingRequest } from "../../Utils/Hooks/useCreateBookingRequest";

const OTP_LENGTH = 6;
const OVERLAY_CLASSES = ["overlay", "overflow-hidden", "overlay-index"];
const createEmptyOtp = () => Array(OTP_LENGTH).fill("");
const getRemainingSeconds = (deadline) =>
  Math.max(0, Math.ceil((Number(deadline) - Date.now()) / 1000));

export const VerifyOtp = () => {
  useValidateBookingSession();

  const navigate = useNavigate();
  const { isSticky, sentinelRef } = useSticky();
  const { isTimesup, handleSessionExpiredClose } = useHoldExpiry();
  const { setFormData, setIsUserVerified, setVerifiedMobile } = useCustomer();
  const { setMembership, setActualMembership } = useAppointment();
  const { resetTimer, startTimer } = useBookingTimer();
  const { setBookingID, setUuid } = useBookingSession();
  const otpInputRefs = useRef([]);
  const otpValueRef = useRef(createEmptyOtp());
  const verificationPendingRef = useRef(false);
  const verificationSucceededRef = useRef(false);
  const mountedRef = useRef(true);
  const expiredRef = useRef(isTimesup);
  expiredRef.current = isTimesup;

  const [challenge] = useState(() => {
    const storedChallenge = getSessionStorageValue(STORAGE_KEYS.OTP_CHALLENGE);
    return storedChallenge && typeof storedChallenge === "object"
      ? storedChallenge
      : null;
  });
  const [otp, setOtp] = useState(createEmptyOtp);
  const [attemptError, setAttemptError] = useState("");
  const [slotUnavailableMessage, setSlotUnavailableMessage] = useState("");
  const [isCustomerBlocked, setIsCustomerBlocked] = useState(false);
  const { releaseHold } = useReleaseBookingHold();
  const [resendAvailableAt, setResendAvailableAt] = useState(
    () => challenge?.resendAvailableAt || 0
  );
  const [resendCountdown, setResendCountdown] = useState(() =>
    getRemainingSeconds(challenge?.resendAvailableAt || 0)
  );

  const mobile = challenge?.mobile || "";
  const countryCode = challenge?.countryCode || "SG";
  const { data: countryDirectory } = useGetCountries();
  const deliveryMethod = challenge?.deliveryMethod || "sms";
  const deliveryLabel = deliveryMethod === "whatsapp" ? "WhatsApp" : "SMS";
  const formattedMobile = formatPhoneNumberIntl(mobile) || mobile;

  const sendAttempts = useOtpApiAttempts(mobile, "send");
  const verifyAttempts = useOtpApiAttempts(mobile, "verify");
  const resendPendingRef = useRef(false);
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  const verifyOTP = useVerifyOTP({ retry: false });
  const customerDetails = useGetCustomerDetails();
  const isValidating = verifyOTP.isPending || customerDetails.isPending || isCreatingBooking;
  // Resending is pointless while verification is locked, so the Resend button
  // shows the longest wait: resend cooldown, send lock, or verify lock.
  const resendWaitSeconds = Math.max(
    resendCountdown,
    sendAttempts.secondsRemaining,
    verifyAttempts.isLocked ? verifyAttempts.secondsRemaining : 0
  );

  const { createBooking } = useCreateBookingRequest({
    onSlotUnavailable: (message) => {
      setIsCreatingBooking(false);
      setSlotUnavailableMessage(message);
    },
    onSuccess: (response) => {
      if (!mountedRef.current || sessionExpired()) return;
      const result = typeof response?.success === "boolean" ? response : response?.data;
      if (result?.success) {
        const bookingId = result?.data?.booking?.id || null;
        const bookingUuid = result?.data?.booking?.uuid || null;
        setBookingID(bookingId);
        if (bookingUuid) setUuid(bookingUuid);
        if (bookingId && bookingUuid) startTimer();
      }
      setIsCreatingBooking(false);
      navigate("/contact-details", { replace: true });
    },
    onError: () => {
      // Booking creation failed — navigate anyway; ContactDetail will retry on submit
      setIsCreatingBooking(false);
      navigate("/contact-details", { replace: true });
    },
  });

  // Blocked customers cannot book online at all — release the held slot and
  // show the Contact Our Team popup instead of an inline error.
  const handleBlockedCustomer = () => {
    setIsCustomerBlocked(true);
    setAttemptError("");
    resetTimer();
    releaseHold();
  };

  const resendSMS = useSendSMS({
    onSuccess: (response) => {
      const locked = sendAttempts.recordResponse(response);
      if (isBlockedCustomerResponse(response)) {
        handleBlockedCustomer();
        return;
      }
      if (response?.success === false) {
        setAttemptError(locked
          ? "" : response?.message || "Unable to resend OTP. Please try again.");
        return;
      }
      otpValueRef.current = createEmptyOtp();
      setOtp(otpValueRef.current);
      setAttemptError("");
      const nextAvailableAt = Date.now() + OTP_RESEND_COOLDOWN_SECONDS * 1000;
      setResendAvailableAt(nextAvailableAt);
      setResendCountdown(OTP_RESEND_COOLDOWN_SECONDS);
      setSessionStorageValue(STORAGE_KEYS.OTP_CHALLENGE, {
        ...challenge,
        resendAvailableAt: nextAvailableAt,
      });
      window.setTimeout(() => otpInputRefs.current[0]?.focus(), 0);
    },
    onError: (error) => {
      const locked = sendAttempts.recordResponse(error);
      if (isBlockedCustomerResponse(error)) {
        handleBlockedCustomer();
        return;
      }
      setAttemptError(locked
        ? "" : error?.response?.data?.message || "Unable to resend OTP. Please try again.");
    },
    onSettled: () => { resendPendingRef.current = false; },
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    // Start OTP verification without a UUID from an earlier booking.
    setUuid(null);
  }, [setUuid]);

  const sessionExpired = () => {
    const deadline = Number(getSessionStorageValue(STORAGE_KEYS.COUNTDOWN_END_TIME));
    return expiredRef.current || (deadline > 0 && Date.now() >= deadline);
  };

  useEffect(() => {
    if (!mobile) {
      navigate("/", { replace: true });
    }
  }, [mobile, navigate]);

  useEffect(() => {
    if (isTimesup || isCustomerBlocked || slotUnavailableMessage) {
      document.body.classList.add(...OVERLAY_CLASSES);
    } else {
      document.body.classList.remove(...OVERLAY_CLASSES);
    }

    return () => document.body.classList.remove(...OVERLAY_CLASSES);
  }, [isTimesup, isCustomerBlocked, slotUnavailableMessage]);

  useEffect(() => {
    if (!resendAvailableAt) return undefined;

    const updateCountdown = () => {
      setResendCountdown(getRemainingSeconds(resendAvailableAt));
    };

    updateCountdown();
    const interval = window.setInterval(updateCountdown, 250);
    return () => window.clearInterval(interval);
  }, [resendAvailableAt]);

  useLayoutEffect(() => {
    if (!mobile || isTimesup) return;
    otpInputRefs.current[0]?.focus({ preventScroll: true });
    // Retry after the route has painted if mounting temporarily dropped focus.
    // Leave focus alone if the user has already moved to another control.
    const frame = window.requestAnimationFrame(() => {
      if (document.activeElement === document.body) {
        otpInputRefs.current[0]?.focus({ preventScroll: true });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isTimesup, mobile]);

  const handleVerifyOtp = async (nextOtp = otpValueRef.current) => {
    if (isCustomerBlocked) return;
    if (!mobile || sessionExpired() || verificationPendingRef.current ||
        verificationSucceededRef.current || verifyOTP.isPending || resendSMS.isPending) return;

    const otpValue = nextOtp.join("");
    if (nextOtp.length !== OTP_LENGTH || !nextOtp.every((digit) => /^\d$/.test(digit))) {
      setAttemptError("Please enter the complete OTP.");
      return;
    }

    if (verifyAttempts.isLockedNow()) return;

    const countryId = getCountryIdByIsoCode(countryCode, countryDirectory?.byCode) || challenge?.countryId;
    if (!countryId) {
      setAttemptError("Country information is unavailable. Please try again shortly or change your number to reload the country list.");
      return;
    }

    // Set the lock synchronously, before React Query or React can re-render.
    verificationPendingRef.current = true;
    setAttemptError("");
    try {
      const response = await verifyOTP.mutateAsync({ mobile_number: mobile, otp: otpValue });
      if (!mountedRef.current || sessionExpired()) return;
      if (response?.success === false || response?.data?.success === false) {
        throw otpResponseError(response, "Invalid OTP. Please try again.");
      }
      verifyAttempts.recordResponse(response);
      verificationSucceededRef.current = true;
      const verificationData = response?.data?.data ?? response?.data;
      const sessionId = verificationData?.session_id;

      // Fetch fresh user profile using the session returned by OTP verification.
      let profileData = verificationData;
      if (sessionId) {
        try {
          const detailsResponse = await customerDetails.mutateAsync({
            mobile_number: mobile,
            session_id: sessionId,
          });
          if (!mountedRef.current || sessionExpired()) return;
          const detailsData = detailsResponse?.data?.data ?? detailsResponse?.data;
          if (detailsData) profileData = detailsData;
        } catch {
          // Non-fatal: fall back to data from the verify response.
        }
      }

      // Support APIs that nest user fields under a `user` key and those that
      // return them flat at the data level.
      const user = profileData?.user ?? profileData;
      const contactMobile = user?.contact_number || profileData?.mobile_number || mobile;
      setFormData((previous) => ({
        ...previous,
        ...(user?.first_name != null ? { fname: user.first_name } : {}),
        ...(user?.last_name != null ? { lname: user.last_name } : {}),
        ...(user?.email != null ? { email: user.email } : {}),
        ...(Object.prototype.hasOwnProperty.call(user, "notes")
          ? { notes: user.notes ?? "" } : {}),
        mobile: contactMobile,
        countryId,
      }));
      setMembership(true);
      const latestMembership = profileData?.is_membership ?? verificationData?.is_membership;
      setActualMembership(latestMembership);
      setOtpSession(mobile, sessionId);
      resetTimer();
      setVerifiedMobile(mobile);
      setIsUserVerified(true);
      removeSessionStorageValue(STORAGE_KEYS.OTP_CHALLENGE);
      setIsCreatingBooking(true);
      createBooking({ mobile_number: contactMobile, actualMembership: latestMembership, customerFirstName: user?.first_name });
    } catch (error) {
      if (!mountedRef.current || sessionExpired()) return;
      if (isBlockedCustomerResponse(error)) {
        handleBlockedCustomer();
        return;
      }
      const locked = verifyAttempts.recordResponse(error);
      setAttemptError(locked
        ? "" : error?.response?.data?.message || error?.message || "Unable to verify OTP. Please try again.");
      otpValueRef.current = createEmptyOtp();
      setOtp(otpValueRef.current);
      otpInputRefs.current[0]?.focus({ preventScroll: true });
    } finally {
      verificationPendingRef.current = false;
    }
  };

  const updateOtp = (startIndex, rawValue, replaceExisting = false) => {
    if (verificationPendingRef.current || verificationSucceededRef.current ||
        isValidating || resendSMS.isPending || sessionExpired()) return;

    const digits = String(rawValue).replace(/\D/g, "");
    if (!digits && rawValue) return;
    const nextOtp = replaceExisting ? createEmptyOtp() : [...otpValueRef.current];
    if (!digits) {
      nextOtp[startIndex] = "";
    } else {
      digits.slice(0, OTP_LENGTH - startIndex).split("").forEach((digit, offset) => {
        nextOtp[startIndex + offset] = digit;
      });
    }

    // Keep state updaters pure: input events, never effects or render, submit OTPs.
    otpValueRef.current = nextOtp;
    setOtp(nextOtp);
    setAttemptError("");
    if (digits) {
      const nextIndex = Math.min(startIndex + digits.length, OTP_LENGTH - 1);
      otpInputRefs.current[nextIndex]?.focus({ preventScroll: true });
    }
    if (nextOtp.every((digit) => /^\d$/.test(digit))) {
      void handleVerifyOtp(nextOtp);
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (verificationPendingRef.current || isValidating || resendSMS.isPending) {
      if (event.key !== "Tab") event.preventDefault();
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      void handleVerifyOtp();
      return;
    }

    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      otpInputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      event.preventDefault();
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    updateOtp(0, event.clipboardData.getData("text"), true);
  };

  const handleOtpBlur = (index, event) => {
    if (isTimesup || isValidating) return;

    const nextFocusedElement = event.relatedTarget;
    const isMovingWithinOtp = otpInputRefs.current.includes(nextFocusedElement);
    const isUsingPageControl = nextFocusedElement?.closest?.("button, a");

    if (isMovingWithinOtp || isUsingPageControl) return;

    window.setTimeout(() => {
      if (otpInputRefs.current.includes(document.activeElement)) return;

      const firstEmptyIndex = otp.findIndex((digit) => !digit);
      otpInputRefs.current[firstEmptyIndex < 0 ? index : firstEmptyIndex]?.focus();
    }, 0);
  };

  const changeMobileNumber = () => {
    removeSessionStorageValue(STORAGE_KEYS.OTP_CHALLENGE);
    navigateWithInputFocus(navigate, "/verify-identity", "booking-otp-mobile");
  };

  const handleBack = () => {
    navigate("/review-and-confirm-booking");
  };

  const resendOtp = () => {
    if (isCustomerBlocked || sessionExpired() || verificationPendingRef.current || isValidating || getRemainingSeconds(resendAvailableAt) > 0 || resendPendingRef.current || resendSMS.isPending || sendAttempts.isLockedNow()) return;
    resendPendingRef.current = true;
    resendSMS.mutate({ mobile_number: mobile });
  };

  const errorMessage = verifyAttempts.message || sendAttempts.message || attemptError;

  if (!mobile) return null;

  return (
    <>
      <VerificationLayout
        className={styles.verifyOtpPage}

      >
        <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
        <div className={`sticky-header ${isSticky ? "is-sticky" : ""}`}>
          <Timer customClass={styles.timer} />
          <Header
            title="Enter OTP"
            description={<>OTP sent to <strong>{formattedMobile}</strong> via {deliveryLabel}.</>}
            onBack={handleBack}
          />
        </div>

        <main className={styles.otpContent}>
          <button
            type="button"
            className={styles.changeNumberButton}
            onClick={changeMobileNumber}
            disabled={isTimesup}
          >
            Change Number
          </button>

          <div className={styles.divider} aria-hidden="true" />

          <div className={styles.otpInputs} role="group" aria-label="Enter OTP" aria-busy={isValidating}>
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`booking-otp-digit-${index}`}
                ref={(element) => {
                  otpInputRefs.current[index] = element;
                }}
                className={styles.otpInput}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={index === 0 ? "one-time-code" : "off"}
                autoFocus={index === 0 && !isTimesup}
                maxLength={index === 0 ? OTP_LENGTH : 1}
                value={digit}
                placeholder="-"
                onChange={(event) => updateOtp(index, event.target.value)}
                onFocus={(event) => event.target.select()}
                onKeyDown={(event) => handleOtpKeyDown(index, event)}
                onPaste={handleOtpPaste}
                onBlur={(event) => handleOtpBlur(index, event)}
                aria-label={`OTP digit ${index + 1} of ${OTP_LENGTH}`}
                aria-invalid={Boolean(errorMessage)}
                aria-describedby={errorMessage ? "otp-attempt-error" : undefined}
                // Keep the focused input enabled so validation does not dismiss
                // the mobile keyboard. updateOtp ignores edits while validating.
                disabled={isTimesup}
              />
            ))}
          </div>

          {errorMessage && (
            <p id="otp-attempt-error" className={styles.attemptError} role="alert">
              {errorMessage}
            </p>
          )}
          <div className={styles.resendPrompt}>
            <span>Didn't receive it?</span>
            <button
              type="button"
              className={styles.resendButton}
              onClick={resendOtp}
              disabled={isTimesup || isValidating || resendWaitSeconds > 0 || resendSMS.isPending || sendAttempts.isLocked || verifyAttempts.isLocked}
            >
             {resendSMS.isPending ? "Sending OTP…" : "Resend OTP"}
              {resendWaitSeconds > 0 && (
                <> in {String(Math.floor(resendWaitSeconds / 60)).padStart(2, "0")}:{String(resendWaitSeconds % 60).padStart(2, "0")}</>
              )}
            </button>
          </div>

          {!verifyAttempts.isLocked && verifyAttempts.attemptsLeft !== null && (
            <p className={styles.resendText}>
              Verification attempts remaining: {verifyAttempts.attemptsLeft}
              {verifyAttempts.maxAttempts !== null ? ` of ${verifyAttempts.maxAttempts}` : ""}
            </p>
          )}
          {!sendAttempts.isLocked && sendAttempts.attemptsLeft !== null && (
            <p className={styles.resendText}>
              SMS attempts remaining: {sendAttempts.attemptsLeft}
              {sendAttempts.maxAttempts !== null ? ` of ${sendAttempts.maxAttempts}` : ""}
            </p>
          )}

          {isValidating && !isTimesup ? (
            <p className={styles.validationStatus} role="status">
              <span className={styles.spinner} aria-hidden="true" />
              We are validating the OTP.
            </p>
          ) : null}
        </main>
      </VerificationLayout>

      {isTimesup ? (
        <SessionExpiredPopup
          active={isTimesup}
          onClose={handleSessionExpiredClose}
        />
      ) : null}
      <SlotUnavailablePopup
        active={!!slotUnavailableMessage}
        message={slotUnavailableMessage}
        onClose={() => setSlotUnavailableMessage("")}
      />
      <BlockedCustomerPopup
        active={isCustomerBlocked}
        onReturnHome={() => navigate("/", { replace: true })}
        onClose={() => setIsCustomerBlocked(false)}
      />
    </>
  );
};

export default VerifyOtp;
